"use client";

import "dayjs/locale/uk";

import { ConfigProvider, Spin } from "antd";
import ukUA from "antd/locale/uk_UA";
import dayjs, { Dayjs } from "dayjs";
import { FC, memo, useEffect, useMemo, useState } from "react";
import {
    getInvoiceSalesByMarketsThunk,
    getInvoiceSalesBySKUThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import FlexContainer from "@/components/FlexContainer";
import { UniversalTabs } from "@/components/UniversalTabs";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { ByMarket } from "./ByMarket";
import { BySKU } from "./BySKU";
import { StyledEmptySalesResultContainer } from "./EmptySalesResult/Styled";
import { StyledSalesDatePicker } from "./Styled";
import { TotalSumByAny } from "./TotalSumByAny";

type TabValue = "sku" | "market";

const salesTabsConfig: { value: TabValue; label: string }[] = [
    { value: "market", label: "По ТТ" },
    { value: "sku", label: "По СКЮ" },
];

dayjs.locale("uk");

interface ISalesProps {
    userId: string;
}

const Sales: FC<ISalesProps> = ({ userId }) => {
    const { marketsSales } = useAppSelector((state) => state.work);
    const [date, setDate] = useState<Dayjs | null>(dayjs());
    const dispatch = useAppDispatch();
    const [currentSalesTab, setCurrentSalesTab] = useState<TabValue>("market");
    const [loading, setLoading] = useState(false);

    const renderSalesTabContent = (tab: TabValue) => {
        if (loading) {
            return (
                <StyledEmptySalesResultContainer>
                    <Spin size="large" />
                </StyledEmptySalesResultContainer>
            );
        }
        switch (tab) {
            case "market":
                return <ByMarket targetDate={date.toISOString()} />;
            case "sku":
                return <BySKU />;
            default:
                return null;
        }
    };

    const totalData = useMemo(() => {
        return marketsSales.reduce(
            (acc, market) => {
                market.invoices.forEach(({ paymentForm, amount }) => {
                    if (paymentForm === "1") acc.form1 += amount;
                    else if (paymentForm === "2") acc.form2 += amount;
                    acc.total += amount;
                });
                return acc;
            },
            { form1: 0, form2: 0, total: 0 },
        );
    }, [marketsSales]);

    useEffect(() => {
        const fetchSales = async () => {
            if (!date) return;
            setLoading(true);
            await dispatch(
                getInvoiceSalesByMarketsThunk({
                    agentId: userId,
                    targetDate: date.format(backendDateFormat.date),
                }),
            );
            await dispatch(
                getInvoiceSalesBySKUThunk({
                    agentId: userId,
                    targetDate: date.format(backendDateFormat.date),
                }),
            );
            setLoading(false);
        };

        fetchSales();
    }, [date, userId, dispatch]);

    return (
        <ConfigProvider locale={ukUA}>
            <FlexContainer
                flexDirection="column"
                gap="16px"
                alignItems="flex-start"
                justifyContent="flex-start"
                width="100%"
            >
                <FlexContainer
                    width="100%"
                    justifyContent="space-between"
                    gap="16px"
                >
                    <UniversalTabs
                        value={currentSalesTab}
                        onChange={(val) => setCurrentSalesTab(val as TabValue)}
                        tabs={salesTabsConfig}
                        maxWidth="159px"
                    />

                    <StyledSalesDatePicker
                        value={date}
                        onChange={(val: Dayjs) => setDate(val)}
                        format={() =>
                            date ? `${date.format("D MMMM YYYY")} року` : ""
                        }
                        allowClear={false}
                        disabledDate={(current) =>
                            current && current > dayjs().endOf("day")
                        }
                    />
                </FlexContainer>
                {!loading && (
                    <TotalSumByAny data={[{ key: "total", ...totalData }]} />
                )}
                {renderSalesTabContent(currentSalesTab)}
            </FlexContainer>
        </ConfigProvider>
    );
};

export default memo(Sales);
